import {
  Box,
  Stack,
  type StackProps,
} from '@ace-clublabs/shared-component-library';
import type React from 'react';
import type { ReactNode } from 'react';

import BlockScreen from '@/components/shared/BlockScreen';
import ProgressBar, {
  type ProgressBarProps,
} from '@/components/shared/ProgressBar';
import useThemeStore from '@/store/ThemeStore';

type SplitPageLayoutProps = {
  leftSide: ReactNode;
  rightSide: ReactNode;
  blockRight?: boolean;
  progressBarProps?: ProgressBarProps;
  blockScreenSx?: StackProps['sx'];
};

const SplitPageLayout: React.FC<SplitPageLayoutProps> = ({
  leftSide,
  rightSide,
  blockRight = false,
  progressBarProps,
  blockScreenSx = [],
}) => {
  const { isLuxuryTheme } = useThemeStore();
  return (
    <Stack
      sx={[
        {
          display: 'flex',
          flexDirection: {
            xs: 'column',
            lg: 'row',
          },
          minHeight: '100%',
          width: '100%',
          backgroundColor: 'grey.100',
        },
        isLuxuryTheme && {
          backgroundColor: 'background.default',
        },
      ]}
    >
      <Stack
        direction='column'
        sx={[
          {
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: {
              xs: '100%',
              lg: '35%',
            },
            backgroundColor: 'grey.50',
            height: {
              xs: 'auto',
              lg: '100vh',
            },
            overflow: {
              xs: 'visible',
              lg: 'auto',
            },
          },
          isLuxuryTheme && {
            backgroundColor: '#16262c',
            backgroundImage:
              'url(/travel/agent/scheduler/luxuryLeftSideBackground.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          },
        ]}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: '100%',
            height: '100%',
          }}
        >
          {leftSide}
        </Box>
      </Stack>
      <Stack
        direction='column'
        sx={[
          {
            justifyContent: 'flex-start',
            alignItems: 'center',
            width: {
              xs: '100%',
              lg: '65%',
            },
            backgroundColor: 'background.default',
            height: {
              xs: 'auto',
              lg: '100vh',
            },
            overflow: {
              xs: 'visible',
              lg: 'auto',
            },
            position: 'relative',
          },
          isLuxuryTheme && {
            backgroundImage:
              'url(/travel/agent/scheduler/luxuryMapBackgroundImage.png)',
            backgroundSize: '170%',
            backgroundPosition: '90% 85%',
          },
        ]}
      >
        <BlockScreen
          sx={[
            { width: '100%', height: '100%', overflow: 'auto' },
            ...(Array.isArray(blockScreenSx) ? blockScreenSx : [blockScreenSx]),
          ]}
          blocked={blockRight}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              alignItems: 'center',
              width: '100%',
              height: '100%',
            }}
          >
            {rightSide}
          </Box>
        </BlockScreen>
        {progressBarProps && <ProgressBar {...progressBarProps} />}
      </Stack>
    </Stack>
  );
};

export default SplitPageLayout;
